import { FileUploadImportConfig } from '@oneschema/react';
import React, { useCallback, useState } from 'react';

import { OneSchemaWrapper } from '@neo.tax/shared';

import { RdProjectBulkUploadInstructionModal } from '../rd-project-bulk-upload-instruction-modal';
import { RD_PROJECT_BULK_UPLOAD_ONESCHEMA_TEMPLATE_KEY } from './bulk-upload.utils';
import { useAddRdProjectsFile } from './hooks';

type RdProjectBulkUploadModalProps = {
  close: () => void;
  companyId: string;
  fiscalYearId: string;
  isOpen: boolean;
};

export const RdProjectBulkUploadModal = ({
  close,
  companyId,
  fiscalYearId,
  isOpen,
}: RdProjectBulkUploadModalProps): React.ReactElement => {
  const [hasLaunched, setHasLaunched] = useState<boolean>(false);
  const [hasConfirmedInstruction, setHasConfirmedInstruction] = useState<boolean>(false);

  const { handleSuccess, oneSchemaJwt, permanentUrl, signedUrl } = useAddRdProjectsFile({
    companyId,
    fiscalYearId,
    isOpen,
  });

  const resetState = useCallback((): void => {
    close();
    setHasConfirmedInstruction(false);
    setHasLaunched(false);
  }, [close]);

  const onCancel = useCallback((): void => {
    resetState();
  }, [resetState]);

  const onError = useCallback((): void => {
    resetState();
  }, [resetState]);

  const onSuccess = useCallback(async (): Promise<void> => {
    await handleSuccess();
    resetState();
  }, [handleSuccess, resetState]);

  const onLaunched = useCallback((): void => {
    setHasLaunched(true);
  }, []);

  const isOneSchemaOpen = isOpen && !!oneSchemaJwt && !!signedUrl && !!permanentUrl && hasConfirmedInstruction;

  const importConfig: FileUploadImportConfig = {
    format: 'csv',
    type: 'file-upload',
    url: signedUrl ?? '',
  };

  return (
    <>
      {!hasLaunched && (
        <RdProjectBulkUploadInstructionModal
          isOpen={isOpen}
          close={onCancel}
          loading={hasConfirmedInstruction}
          submit={(): void => setHasConfirmedInstruction(true)}
        />
      )}
      <OneSchemaWrapper
        isOpen={isOneSchemaOpen}
        userJwt={oneSchemaJwt ?? ''}
        templateKey={RD_PROJECT_BULK_UPLOAD_ONESCHEMA_TEMPLATE_KEY}
        importConfig={importConfig}
        onCancel={onCancel}
        onError={onError}
        onSuccess={onSuccess}
        onLaunched={onLaunched}
      />
    </>
  );
};
